/**
 * The route table — GENERATED FROM `PAGE_REGISTRY`, never hand-listed.
 *
 * `lib/rbac/pages.ts` is the single source of truth for which pages exist,
 * which SIX of them are nav entries (`NAV_PAGES`, V5-33), and which are routed
 * but reached from inside another page (`hideFromNav` + `parent`).
 * `lib/rbac/route-guard.ts` gates by looking a pathname up in that same
 * registry. If the SPA hand-wrote its routes it could silently gain a route the
 * registry does not know about — and a route the registry does not know about
 * has NO PERMISSION CHECK, which is precisely the trap the registry's own
 * comment warns of.
 *
 * So: every registry entry becomes a route, in registry order. Each page
 * manifest supplies an ELEMENT for a registry key, never a new path. The
 * exceptions below (`/settings*`, `/admin*`) are not registry keys at all;
 * they are role-gated by `isAdminOnlyPath`, which decides BEFORE the registry
 * is consulted, so they need no page key to be safe.
 *
 * The V5-33 restructure changed the NAVBAR, not this file's rule: seven pages
 * became `hideFromNav`, and every one of them still gets a route here and a
 * gate from the same registry row it always had. `PagePlaceholder` is kept —
 * a registry entry added without a corresponding port (the vault, today)
 * renders something honest instead of a blank screen.
 */
import { Suspense, type ReactNode } from "react";
import { Navigate, useRoutes, type RouteObject } from "react-router-dom";
import { PAGE_REGISTRY } from "@/lib/rbac/pages";
import { ForcePasswordChangeGate } from "@/pages/ForcePasswordChangePage";
import { RequireAuth, RedirectIfAuthenticated } from "@/components/auth/RequireAuth";
// The Wallet-parity shell. `AppShell` picks between this and `DashboardLayout`
// at RUNTIME from `wallet/shell/flag.ts`, which currently defaults to the
// classic one -- so mounting it changes nothing for anybody until the flag is
// flipped. What it does buy is `?shell=wallet`, which lets the two be compared
// back to back on a real device without a build.
//
// Kept a STATIC import (not lazy) on purpose: it is the app chrome itself —
// nav, header, the auth-gated `<Outlet/>` — so a Suspense boundary around it
// would blank the whole screen (nav included) on every navigation instead of
// just the content area. `lazyNamed` below handles the actual page weight.
import { AppShell } from "@/wallet/shell";
import { LoginPage } from "@/pages/LoginPage";
import { NoAccessPage } from "@/pages/NoAccessPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { PagePlaceholder } from "@/pages/PagePlaceholder";
import { AccessRequestPendingPage } from "@/pages/AccessRequestPendingPage";
import { lazyNamed, RouteSkeleton } from "@/lib/prefetch";

// Wave 6 — Groups & split bills, and the public receipt page.
const GroupsListPage = lazyNamed(() => import("@/pages/groups/GroupsListPage"), "GroupsListPage");
const GroupDetailPage = lazyNamed(() => import("@/pages/groups/GroupDetailPage"), "GroupDetailPage");
const SharePublicPage = lazyNamed(() => import("@/pages/share/SharePublicPage"), "SharePublicPage");

import { coreRoutes, CORE_PAGE_KEYS } from "@/pages/core.routes";
import { INSIGHTS_PAGES } from "@/pages/insights.routes";
import { ITRSection, settingsRoutes } from "@/pages/itr.routes";
import { rentCoreRoutes, RentLayout } from "@/pages/rent-core.routes";
import { rentExtrasRoutes, AdminRoutes } from "@/pages/rent-extras.routes";

// S3: lazy — these leaves are reached from ONE nav item each (or none, for
// the admin-only/settings-only ones), so nobody should pay for all of them on
// first load. Same `lazyNamed` adapter every `*.routes.tsx` manifest uses.
const NotificationsPage = lazyNamed(
  () => import("@/pages/notifications/NotificationsPage"),
  "NotificationsPage",
);
// Wave 3 (W12). The gate is a STATIC import — it wraps the whole app, and a
// lazy wrapper would gate the gate behind a chunk load. SecurityPage is lazy
// like every other leaf.
// eslint-disable-next-line import/first
const SecurityPage = lazyNamed(
  () => import("@/pages/security/SecurityPage"),
  "SecurityPage",
);
const UserSettingsPage = lazyNamed(
  () => import("@/pages/settings/UserSettingsPage"),
  "UserSettingsPage",
);
const AccountsPage = lazyNamed(() => import("@/pages/accounts/AccountsPage"), "AccountsPage");
const AccountDetailPage = lazyNamed(
  () => import("@/pages/accounts/AccountDetailPage"),
  "AccountDetailPage",
);
const RecycleBinPage = lazyNamed(
  () => import("@/pages/recycle-bin/RecycleBinPage"),
  "RecycleBinPage",
);
// `reports.routes.tsx` (owned by another agent this wave) statically imports
// `ReportsPage` itself; importing that same leaf page directly here — rather
// than the manifest's `reportsRoutes` — keeps the identical single path
// (`reports/*`) while making it lazy, with no edit to that file.
const ReportsPage = lazyNamed(() => import("@/pages/reports/ReportsPage"), "default");
const InvestPage = lazyNamed(() => import("@/pages/invest/InvestPage"), "InvestPage");
// v5.0 family document vault + warranties (V5-14..V5-22). One `vault`
// registry key, three tabs inside; `hideFromNav`, reached from Records.
const VaultPage = lazyNamed(() => import("@/pages/vault/VaultPage"), "VaultPage");
const TemplatesPage = lazyNamed(() => import("@/pages/templates/TemplatesPage"), "TemplatesPage");
// FD / loan EMI / prepayment / ITR calculators (2026-10-09). Not a registry
// key -- see the comment on the route below.
const CalculatorsPage = lazyNamed(() => import("@/pages/calculators/CalculatorsPage"), "CalculatorsPage");

/** One Suspense boundary per route element — matches every `*.routes.tsx` manifest. */
function s(element: ReactNode): ReactNode {
  return <Suspense fallback={<RouteSkeleton />}>{element}</Suspense>;
}

/**
 * `/rent` is the one prefix two agents ported. Core (dashboard, properties,
 * tenants, agreements, payments) and extras (tds, statements, brokers,
 * reports, …) are concatenated under the single `RentLayout`, which mounts the
 * SubNav and the payment FAB once — the same thing the Next.js rent layout did.
 * Mounting two layouts would give two SubNavs and two FABs.
 */
/**
 * `/accounts` needs two paths (list and detail) but must still present as the
 * single `accounts/*` registry route — a flat pair would leave the registry key
 * unrouted by the parity check, which is the exact drift that check exists to
 * catch. Nesting keeps one registry entry, one route.
 */
function AccountsSection() {
  return useRoutes([
    { index: true, element: s(<AccountsPage />) },
    { path: ":id", element: s(<AccountDetailPage />) },
  ]);
}

function RentSection() {
  return useRoutes([{ element: <RentLayout />, children: [...rentCoreRoutes, ...rentExtrasRoutes] }]);
}

/**
 * Which registry keys have a real implementation. `CORE_PAGE_KEYS` are handled
 * separately below because two of them (`/accounts`, `/accounts/:id`) need flat
 * sibling paths rather than a splat — a `/*` path cannot carry children.
 *
 * TO REGISTER A NEW PAGE, two edits and no third:
 *
 *   1. add a row to `PAGE_REGISTRY` in `lib/rbac/pages.ts` (key, label, href,
 *      icon, matchPrefix; plus `hideFromNav` + `parent` unless it is one of the
 *      six nav destinations, which are FIXED — a new feature is a sub-page or a
 *      tab, never a seventh entry);
 *   2. add `<key>: <YourPage />` to this map.
 *
 * Do NOT add a route to the arrays below — `registryRoutes` derives the path
 * from the registry's own `matchPrefix`, which is what guarantees every route
 * has a permission check. A key with no entry here still routes; it renders
 * `PagePlaceholder`, which is honest rather than blank.
 */
const IMPLEMENTED: Partial<Record<string, React.ReactNode>> = {
  ...INSIGHTS_PAGES,
  itr: <ITRSection />,
  rent: <RentSection />,
  accounts: <AccountsSection />,
  invest: s(<InvestPage />),
  vault: s(<VaultPage />),
  templates: s(<TemplatesPage />),
};

/** `"/yearly-summary"` -> `"yearly-summary/*"` (react-router wants no leading slash on children). */
function toChildPath(matchPrefix: string): string {
  return `${matchPrefix.replace(/^\//, "")}/*`;
}

const CORE_KEYS = new Set<string>(CORE_PAGE_KEYS.filter((k) => k !== "accounts"));

/** The two flat accounts entries are superseded by `AccountsSection`. */
const coreRoutesWithoutAccounts = coreRoutes.filter(
  (r) => !String(r.path ?? "").startsWith("accounts"),
);

const registryRoutes: RouteObject[] = PAGE_REGISTRY.filter(
  (page) => !CORE_KEYS.has(page.key),
).map((page) => ({
  path: toChildPath(page.matchPrefix),
  element: IMPLEMENTED[page.key] ?? <PagePlaceholder page={page} />,
}));

export const routes: RouteObject[] = [
  // Public — the two paths `middleware.ts` lets through without a session.
  {
    path: "/login",
    element: (
      <RedirectIfAuthenticated>
        <LoginPage />
      </RedirectIfAuthenticated>
    ),
  },
  { path: "/access-request-pending", element: <AccessRequestPendingPage /> },

  // A shared receipt is readable by a friend who has NO AIM account — the
  // token in the URL is the credential, which is the entire point of the
  // feature. It therefore sits outside RequireAuth and outside AppShell (no
  // nav, no chrome: this viewer is not a user of the app, they are a person
  // who was sent a bill). The server returns an allow-listed projection, so
  // this page cannot render more than the receipt even if it tried.
  { path: "/share/:token", element: s(<SharePublicPage />) },

  // Everything else lives behind the guard, inside the app chrome.
  {
    element: (
      <RequireAuth>
        <ForcePasswordChangeGate>
          <AppShell />
        </ForcePasswordChangeGate>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      // Core daily pages — supplied as routes rather than elements so
      // `/accounts/:id` can be a real sibling path.
      ...coreRoutesWithoutAccounts,
      ...registryRoutes,
      // Groups & split bills. Not a navbar entry — the 7-entry navbar is a
      // locked decision, so Groups is reached from the dashboard and the
      // avatar menu rather than becoming an eighth tab.
      {
        path: "groups",
        children: [
          { index: true, element: s(<GroupsListPage />) },
          { path: ":id", element: s(<GroupDetailPage />) },
        ],
      },
      // Not registry keys on either stack — gated by `isAdminOnlyPath` in the
      // route guard, mirroring how `middleware.ts` gates them today.
      // V5-27/V5-28 Recycle Bin. `PAGE_REGISTRY` has no `recycle-bin` key, so
      // it cannot be an `IMPLEMENTED` entry — it mounts under `/settings` like
      // the other non-registry admin surfaces and is gated by the same
      // `isAdminOnlyPath` rule. Listed BEFORE `settingsRoutes` for readability;
      // the static segment outranks that array's `settings/*` either way.
      { path: "settings/recycle-bin", element: s(<RecycleBinPage />) },
      // V5-13 multi-account SMTP. Was its own route rendering `MailSettingsPage`
      // directly; Wave 6 (B5) folds it into the `/admin` hub's System > Mail
      // tab (which renders this SAME component — nothing about the mail
      // config UI changed, only the URL an admin reaches it from). Redirected,
      // not deleted, so any bookmark still lands on the working screen.
      { path: "settings/mail", element: <Navigate to="/admin/system?tab=mail" replace /> },
      // V5-30/V5-31/V5-32 Reports. Like the Recycle Bin above, it is not a
      // `PAGE_REGISTRY` key -- and unlike it, it needs no admin gate either,
      // because it holds no data of its own: every report is fetched under the
      // permission of the PAGE it summarises (analytics / accounts / loans /
      // yearly-summary / rent), enforced server-side on every request. See
      // `pages/reports/reports.routes.tsx` for why a seventh permission key
      // would be strictly worse than borrowing the five that already exist.
      // (Path kept byte-identical to that manifest's own `reportsRoutes`;
      // see the `ReportsPage` import above for why it's inlined rather than
      // spread from there.)
      { path: "reports/*", element: s(<ReportsPage />) },
      // `/calculators` -- FD, loan EMI, prepayment and ITR calculators, reached
      // from the Loans / ITR / Analytics headers (never a nav entry). Like
      // `/reports`, deliberately NOT a registry key: it holds no family data
      // (the maths runs in the browser; the ITR compare endpoint is
      // authenticated-only and reads nothing stored), and a key would make a
      // missing `PagePermission` row deny every non-admin a calculator.
      // Listed in `SHELL` in tests/parity.test.ts.
      { path: "calculators", element: s(<CalculatorsPage />) },
      ...settingsRoutes,
      { path: "admin/*", element: <AdminRoutes /> },
      // `/preferences` — every signed-in user's own theme + privacy settings
      // (V5-12/V5-36). Not in the registry ON PURPOSE, and not under
      // `/settings`, which `isAdminOnlyPath` gates on role ADMIN: six of the
      // eight people here are not admins and all eight need a theme switch.
      // A registry key would also mint a `PagePermission` row, making "may
      // this user choose dark mode" revocable, which it must not be. This is
      // the guard's branch 5 — authenticated, unregistered, not admin-prefixed.
      { path: "preferences", element: s(<UserSettingsPage />) },
      // Own-account security — deliberately unregistered, like /preferences.
      { path: "security", element: s(<SecurityPage />) },
      // Not in the registry on purpose — `middleware.ts` lets an authenticated
      // user reach `/no-access` with no permission check, and so does this.
      { path: "no-access", element: <NoAccessPage /> },
      // Same exception, same reasoning: a user's own notification inbox is
      // per-recipient chrome, not a page of family data. Registering a
      // `notifications` page key would DENY every non-admin their own bell
      // until an admin granted it (a missing `PagePermission` row is a deny),
      // which inverts what the feature is for. `guardRoute` allows an
      // authenticated user here; the per-user scoping is enforced server-side
      // in `backend/api/app/services/notifications.py`. Listed in `SHELL` in
      // tests/parity.test.ts.
      { path: "notifications", element: s(<NotificationsPage />) },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];
